import { useCallback } from 'react';
import { ErrorMessageBuilder } from '../../QnAConfigModal/services/ErrorMessageBuilder';
import { ChatMessage } from '../types';
import { UseErrorHandlerReturn } from './useErrorHandler';

interface UseRetryLogicParams {
  errorHandler: UseErrorHandlerReturn;
  getMessagesForAI: () => Array<{ role: 'user' | 'assistant'; content: string }>;
  sendMessageToAI: (
    messages: Array<{ role: 'user' | 'assistant'; content: string }>,
    context: string | undefined,
    onMessageUpdate: (messageId: string, content: string) => void,
    messageId: string,
  ) => Promise<void>;
  updateMessage: (messageId: string, content: string) => void;
  addMessage: (message: ChatMessage) => void;
  context: string | null;
}

export function useRetryLogic({
  errorHandler,
  getMessagesForAI,
  sendMessageToAI,
  updateMessage,
  addMessage,
  context,
}: UseRetryLogicParams) {
  const retryLastAction = useCallback(async () => {
    const {
      lastFailedAction,
      lastFailedMessage,
      lastSuccessCallback,
      setRetrying,
      setError,
      setFailedMessage,
      setFailedAction,
      setSuccessCallback,
    } = errorHandler;

    setRetrying(true);
    setError(null);

    if (lastFailedAction === 'message' && lastFailedMessage) {
      const originalSuccessCallback = lastSuccessCallback;

      try {
        const aiMessages = getMessagesForAI();
        const retryAiMessageId = `ai-${Date.now()}`;
        const retryPlaceholderMessage: ChatMessage = {
          id: retryAiMessageId,
          type: 'ai',
          content: '',
          timestamp: new Date(),
        };

        addMessage(retryPlaceholderMessage);

        await sendMessageToAI(
          aiMessages,
          context || undefined,
          updateMessage,
          retryAiMessageId,
        );

        setRetrying(false);
        setError(null);
        setFailedMessage(null);
        setFailedAction(null);
        setSuccessCallback(null);

        if (originalSuccessCallback) {
          originalSuccessCallback();
        }
      } catch (error) {
        const retryErrorMessage: ChatMessage = {
          id: `system-${Date.now()}`,
          type: 'system',
          content: 'Retry failed. Please try again.',
          timestamp: new Date(),
        };

        const userFriendlyError = ErrorMessageBuilder.buildUserFriendlyMessage(error);

        addMessage(retryErrorMessage);
        setRetrying(false);
        setError(userFriendlyError);
        setFailedMessage(lastFailedMessage);
        setFailedAction('message');
        setSuccessCallback(originalSuccessCallback);
      }
    } else if (lastFailedAction === 'context') {
      // Context reload logic is now in the UI, so this is a no-op.
      // We might want to show a notification to the user.
      setRetrying(false);
    }
  }, [
    errorHandler,
    getMessagesForAI,
    context,
    sendMessageToAI,
    updateMessage,
    addMessage,
  ]);

  return { retryLastAction };
}
